import {Component, DestroyRef, inject, OnInit, ViewChild} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {BehaviorSubject, defer, finalize} from 'rxjs';
import {filter, switchMap, take, tap} from 'rxjs/operators';
import * as R from 'ramda';
import * as RA from 'ramda-adjunct';
import {KEYS, TreeNode, TreeModel} from '@ali-hm/angular-tree-component';
import {
    AddItemToMenuDialogComponent
} from '@emm/shared/dialogs/add-item-to-menu-dialog/add-item-to-menu-dialog.component';
import {operatorLocksFilterFieldMap as fieldMap} from '@emm/shared/formly/maps/operator-locks-filter.config';
import {OperatorLocksFormComponent} from '../emergency-items-form/operator-locks-form.component';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {ContextMenuAction} from '@emm/core/models/context-menu-action.model';
import {AuthFacadeService} from '@emm/auth/services/auth-facade.service';
import {DialogService} from '@emm/shared/services/dialog.service';
import {NotificationService} from '@emm/shared/services/notification.service';
import {OperatorLocksFacadeService} from '@emm/management/services/operator-locks-facade.service';
import {
    AddLockItemDialogComponent
} from '@emm/shared/dialogs/add-emergency-item-dialog/add-lock-item-dialog.component';
import {SearchFilter} from '@emm/core/models/search-filter';
import {UserService} from '@emm/core/services/user.service';
import {
    DragAndDropFileUploadComponent
} from '@emm/shared/dialogs/drag-and-drop-file-upload/drag-and-drop-file-upload.component';
import {API_ERRORS} from '@emm/core/util/api-constants';

const WIDTH_500 = '500px';
const WIDTH_600 = '600px';

@Component({
    selector: 'emm-operator-locks',
    styleUrls: ['./operator-locks.component.scss'],
    templateUrl: './operator-locks.component.html'
})
export class OperatorLocksComponent implements OnInit {

    @ViewChild(OperatorLocksFormComponent) emergencyItemsForm: OperatorLocksFormComponent;

    centerLoading$ = this.operatorLocksFacadeService.centerLoading$;
    private readonly destroyRef: DestroyRef = inject(DestroyRef);
    emergencyItemDetail$ = this.operatorLocksFacadeService.emergencyItemDetail$;
    isSuperAdmin = this.userService.hasSuperAdminRole();
    isAdmin = this.userService.hasAdminRole();
    isAuthor = this.userService.hasAuthorRole();
    nodesToExpand: string[] = [];

    emergencyItemsContextMenuActions = [
        {
            click: (node) => this.addChildItem(node),
            enabled: R.T,
            icon: R.always('add'),
            text: R.always('Add Item'),
            visible: (node) => this.isSuperAdmin || this.isAdmin ? (!node?.isRoot &&
                OperatorLocksComponent.isLevel2(node) && !OperatorLocksComponent.isMMSNode(node)) ||
                node?.parent?.data?.id === 'MMS Options' : R.F,
        },
        {
            click: (node) => this.uploadList(node),
            enabled: R.T,
            icon: R.always('add'),
            text: R.always('Upload List'),
            visible: (node) => this.isSuperAdmin || this.isAdmin ? (!node?.isRoot &&
                OperatorLocksComponent.isLevel2(node) && !OperatorLocksComponent.isMMSNode(node)) ||
                node?.parent?.data?.id === 'MMS Options' : R.F,
        },
        {
            click: (node) => node.data.data.active ?
                this.toggleDeactivate(node, node?.data?.data?.tag, node?.data?.data?.statusMessage,
                    node?.data?.data?.subtype) :
                this.toggleReactivate(node, node?.data?.data?.tag, node?.data?.data?.statusMessage,
                    node?.data?.data?.subtype),
            enabled: R.T,
            icon: (node) => node.data.data.active ? 'visibility_off' : 'visibility',
            text: (node) => node.data.data.active ? 'Deactivate' : 'Reactivate',
            visible: (node) => this.isSuperAdmin || this.isAdmin ? !node?.isRoot &&
                OperatorLocksComponent.isLevel2(node) && !OperatorLocksComponent.isMMSNode(node) : R.F,
        },
        {
            click: (node) => this.deleteItem(node),
            enabled: (node) => OperatorLocksComponent.isChildItem(node),
            icon: R.always('delete'),
            text: R.always('Remove'),
            visible: (node) => this.isSuperAdmin || this.isAdmin ?
                OperatorLocksComponent.isChildItem(node) && !OperatorLocksComponent.isLevel2(node) &&
                !OperatorLocksComponent.isMMSNode(node) : R.F
        },
        {
            click: (node) => this.refreshNodes(node),
            enabled: R.T,
            icon: R.always('refresh'),
            text: R.always('Refresh'),
            visible: (node) => this.isSuperAdmin || this.isAdmin ? R.T : R.F,
        },
    ] as ContextMenuAction<TreeNode>[];

    fields = [fieldMap['name'](), fieldMap['tag']()];

    isReadOnly$ = this.authFacade.isReadOnly$;

    nodes = [];
    nodes$ = new BehaviorSubject<any[]>(undefined);
    nodesCache = [];

    options = {
        actionMapping: {
            mouse: {
                click: (tree: TreeModel, node: TreeNode) => {
                    if (node?.data?.data) {
                        this.operatorLocksFacadeService.select(node?.data?.data);
                        node.setIsActive(true);
                    }
                }
            },
            keys: {
                [KEYS.ENTER]: (tree, node) => alert(`This is ${node?.data?.name}`)
            }
        },
        getChildren: (node: TreeNode) => {
            if (node?.data?.data?.type === 'EMERGENCY') {
                return this.operatorLocksFacadeService.getItemsByEmergency(node?.data?.data?.tag).toPromise();
            }
        }
    };

    static isLevel2(node: TreeNode) {
        return node?.parent && node.parent?.parent?.parent === null;
    }

    static isChildItem(node: TreeNode): boolean {
        return node?.hasChildren === false;
    }

    static isMMSNode(node: TreeNode): boolean {
        if (node?.id?.includes('MMS')) {
            return true;
        }
    }

    constructor(
        private authFacade: AuthFacadeService,
        public dialog: MatDialog,
        private dialogService: DialogService,
        private notificationService: NotificationService,
        private operatorLocksFacadeService: OperatorLocksFacadeService,
        private userService: UserService,
    ) {
    }


    ngOnInit(): void {
        this.operatorLocksFacadeService.init();
        this.centerLoading$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
        this.getEmergencyItems();
    }


    getEmergencyItems(): void {
        this.operatorLocksFacadeService.statuses$
            .pipe(
                tap(() => this.operatorLocksFacadeService.startProgressBar()),
                finalize(() => this.operatorLocksFacadeService.stopProgressBar()),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe({
                next: (statuses) => {
                    this.nodes = statuses;
                    this.nodes$.next(statuses);
                    this.nodesCache = statuses;
                    setTimeout(() => {
                        this.operatorLocksFacadeService.stopProgressBar();
                    }, 500);
                },
                error: (error) => {
                    this.notificationService.error(error?.error?.error?.message || API_ERRORS.GenericErrorMessage);
                }
            });
    }


    openCreateEmergencyItemDialog(): void {
        const dialogRef = this.dialog.open(AddLockItemDialogComponent, {
            width: '600px',
            data: {},
        });

        dialogRef.afterClosed()
            .pipe(
                filter((result) => result && result === 'submit'),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe({
                next: () => {
                    this.notificationService.success('Added Operator Lock Successfully');
                    this.operatorLocksFacadeService.init();
                    this.getEmergencyItems();
                },
                error: (error) => {
                    this.notificationService.error(error?.error?.error?.message || API_ERRORS.GenericErrorMessage);
                }
            });
    }

    getNodesByLockGroupName(value: string) {
        // TODO: this works, but finish this when we have the new backend for searching generic trees
        const topNodes = this.nodesCache.filter(node => {
            const filteredNodes = node.children?.filter(childNode => childNode.name.toLowerCase().includes(value.toLowerCase()));
            return filteredNodes.length > 0
        });
        topNodes.forEach(node => {
            while (node.children?.some(childNode => !childNode.name.toLowerCase().includes(value.toLowerCase()))) {
                node.children?.splice(node.children?.findIndex(childNode => !childNode.name.toLowerCase().includes(value.toLowerCase())), 1);
            }
        })
        console.log(`Hopefully filtered nodes & children:`, topNodes)
        return topNodes;
    }

    getNodesByItemName(value: string) {
        // TODO: this doesn't work, but we will finish this when we have the new backend for searching generic trees
        const topNodes = this.nodesCache.filter(node => {
            const filteredNodes = node.children?.filter(childNode => childNode.name.toLowerCase().includes(value.toLowerCase()));
            return filteredNodes.length > 0
        });
        // const immediateChildren = [];
        topNodes.forEach(node => {
            // need to extract this loop into a function
            while (node.children?.some(childNode => !childNode.name.toLowerCase().includes(value.toLowerCase()))) {
                node.children?.splice(node.children?.findIndex(childNode => !childNode.name.toLowerCase().includes(value.toLowerCase())), 1);
            }
        })
        console.log(`Hopefully filtered nodes & children:`, topNodes)
        return topNodes;
    }


    onFilter(searchFilter: SearchFilter): void {
        this.filterNodes(searchFilter.value as string, searchFilter.key);
    }

    filterNodes(value: string, selected: string): void {
        this.operatorLocksFacadeService.startProgressBar();
        if (RA.isNonEmptyString(value)) {
            this.nodes = [];
            const parentNodes = this.nodesCache.filter(node => node.children?.length > 0 &&
                node.name !== 'Master Menu Selections');
            const MMSParentNodes = this.nodesCache.filter(node =>
                node.name === 'Master Menu Selections')[0]?.children?.forEach(node => parentNodes.push(node));
            this.nodes = [];
            selected === 'tag' ? parentNodes.forEach(parentNode => {
                parentNode.children.filter(node => {
                    if (node.id.toLowerCase().includes(value.toLowerCase())) {
                        this.nodes.push(node);
                    }
                });
            }) : parentNodes.forEach(parentNode => {
                parentNode.children?.filter(node => {
                    if (node[selected].toLowerCase().includes(value.toLowerCase())) {
                        this.nodes.push(node);
                    }
                });
            });
            this.nodes$.next(this.nodes);
            setTimeout(() => {
                this.operatorLocksFacadeService.stopProgressBar();
            }, 500);
        } else {
            this.nodes$.next(this.nodesCache);
            setTimeout(() => {
                this.operatorLocksFacadeService.stopProgressBar();
            }, 500);
        }
    }


    addChildItem(node: TreeNode): void {
        const emergencyTag = node?.data?.data?.tag;
        const dialogRef = this.dialog.open(AddItemToMenuDialogComponent, {
            width: WIDTH_600,
            data: {
                itemTypeFilter: null,
                displayAddGlobal: false,
                emergencyTag
            }
        });
        dialogRef
            .afterClosed()
            .pipe(
                filter((newItem) => newItem && newItem.value),
                switchMap((newItem) =>
                    this.operatorLocksFacadeService.api.addEmergencyItems(emergencyTag, newItem.value)
                ),
                switchMap(() => defer(() => node.loadNodeChildren())),
                take(1),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe({
                    next: () => {
                        this.notificationService.success('Added successfully')
                        this.getEmergencyItems();
                    },
                    error: (error) => {
                        this.notificationService.error(error?.error?.error?.message || API_ERRORS.GenericErrorMessage);
                    }
                }
            );
    }

    addEmergencyItems(emergencyTag, value) {
        this.operatorLocksFacadeService.api.addEmergencyItems(emergencyTag, value).subscribe(
            {
                next() {
                    this.notificationService.success('Added successfully');
                },
                error(error) {
                    this.notificationService.error(error?.error?.error?.message || API_ERRORS.GenericErrorMessage);
                }
            }
        )
    }

    uploadList(node: TreeNode): void {
        const emergencyTag = node?.data?.data?.tag;
        const dialogRef = this.dialog.open(DragAndDropFileUploadComponent, {
            width: WIDTH_500,
            data: {
                itemTypeFilter: null,
                displayAddGlobal: false,
                emergencyTag
            }
        });
        dialogRef
            .afterClosed()
            .pipe(
                filter((file) => file),
                switchMap((file) =>
                    this.operatorLocksFacadeService.api.uploadOperatorLockItems(emergencyTag, file)
                ),
                switchMap(() => defer(() => node.loadNodeChildren())),
                take(1),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe(
                {
                    next: () => {
                        this.notificationService.success('Added successfully');
                        this.getEmergencyItems();
                    }, error: (error) => {
                        this.notificationService.error(error?.error?.error?.message || API_ERRORS.GenericErrorMessage);
                    }
                }
            )
        ;
    }

    refreshNodes(node: TreeNode) {
        this.operatorLocksFacadeService.startProgressBar();
        this.nodesToExpand.push(node?.parent?.id);
        this.nodes$.next([]);
        this.operatorLocksFacadeService.init();
        finalize(() => this.operatorLocksFacadeService.stopProgressBar());
    }

    toggleDeactivate(node: TreeNode, tag: string, statusMessage: string, subType: string): any {
        this.dialogService
            .openConfirmDialog('Are you sure you want to deactivate', node.data.name)
            .afterClosed()
            .pipe(
                filter(RA.isTruthy),
                switchMap(() =>
                    defer(() => this.operatorLocksFacadeService.api.updateStatus(tag, statusMessage, false))
                ),
                tap(() => this.operatorLocksFacadeService.startProgressBar()),
                takeUntilDestroyed(this.destroyRef)
            ).subscribe({
            next: () => {
                this.refreshNodes(node);
                this.notificationService.success(`Successfully deactivated ${tag}. Please wait for the tree to reload.`);
                finalize(() => this.operatorLocksFacadeService.stopProgressBar());
            },
            error: (error) => {
                this.notificationService.error(error?.error?.error?.message || API_ERRORS.GenericErrorMessage);
            }
        });
    }


    toggleReactivate(node: TreeNode, tag: string, statusMessage: string, subType: string): any {
        this.dialogService
            .openConfirmDialog('Are you sure you want to reactivate', node.data.name)
            .afterClosed()
            .pipe(
                filter(RA.isTruthy),
                switchMap(() =>
                    defer(() => this.operatorLocksFacadeService.api.updateStatus(tag, statusMessage, true))
                ),
                tap(() => this.operatorLocksFacadeService.startProgressBar()),
                takeUntilDestroyed(this.destroyRef)
            ).subscribe({
            next: () => {
                this.refreshNodes(node);
                this.notificationService.success(`Successfully reactivated ${tag}. Please wait for the tree to reload.`);
                finalize(() => this.operatorLocksFacadeService.stopProgressBar());
            },
            error: (error) => {
                this.notificationService.error(error?.error?.error?.message || API_ERRORS.GenericErrorMessage);
            }
        });
    }


    deleteItem(node: TreeNode): void {
        const item = node?.data;
        const parent = node?.parent?.data?.data;
        this.dialogService
            .openConfirmDialog('Are you sure you want to remove', item?.name)
            .afterClosed()
            .pipe(
                filter(RA.isTruthy),
                switchMap(() =>
                    defer(() => this.operatorLocksFacadeService.api.deleteEmergencyItem(parent.tag, item.tag))
                ),
                switchMap(() => defer(() => node.parent.loadNodeChildren())),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe({
                next: () => {
                    this.notificationService.success(`Successfully deleted ${item.name}`);
                    this.getEmergencyItems();
                },
                error: (error) => {
                    this.notificationService.error(error?.error?.error?.message || API_ERRORS.GenericErrorMessage);
                }
            });
    }

}
